import { useEffect, useState, ImgHTMLAttributes, SyntheticEvent } from 'react';
import { cn } from '@/lib/utils';

export interface SafeImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  /** Fallback image URL if the main src fails to load. Defaults to /placeholder.svg */
  fallbackSrc?: string;
  /** Class names applied to the wrapping div (controls aspect/size of the skeleton). */
  wrapperClassName?: string;
  /** Disable the loading skeleton (e.g. for tiny avatars/icons). */
  showSkeleton?: boolean;
  /**
   * Render image immediately at full opacity without fade-in (and without skeleton).
   * Use for above-the-fold LCP images where the fade-in would delay paint.
   */
  eager?: boolean;
}

/**
 * SafeImage — wrapper around <img> that:
 *  - shows a shimmering skeleton while loading
 *  - swaps in a fallback image on error (default: /placeholder.svg)
 *  - guards against falsy/empty src values
 */
export default function SafeImage({
  src,
  fallbackSrc = '/placeholder.svg',
  className,
  wrapperClassName,
  showSkeleton = true,
  eager = false,
  onLoad,
  onError,
  alt = '',
  ...rest
}: SafeImageProps) {
  const initial = src && String(src).trim().length > 0 ? String(src) : fallbackSrc;
  const [currentSrc, setCurrentSrc] = useState<string>(initial);
  const [loaded, setLoaded] = useState(eager);
  const [errored, setErrored] = useState(false);

  // React to src prop changes (e.g. lightbox swapping images)
  useEffect(() => {
    const next = src && String(src).trim().length > 0 ? String(src) : fallbackSrc;
    setCurrentSrc(next);
    setLoaded(eager);
    setErrored(false);
  }, [src, fallbackSrc, eager]);

  const handleError = (e: SyntheticEvent<HTMLImageElement>) => {
    if (!errored && currentSrc !== fallbackSrc) {
      setErrored(true);
      setCurrentSrc(fallbackSrc);
    }
    onError?.(e);
  };

  const handleLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    setLoaded(true);
    onLoad?.(e);
  };

  return (
    <span
      className={cn(
        'relative block overflow-hidden bg-muted/40',
        wrapperClassName,
      )}
    >
      {showSkeleton && !eager && !loaded && (
        <span
          aria-hidden="true"
          className="absolute inset-0 animate-pulse bg-gradient-to-br from-muted/50 via-muted/30 to-muted/50"
        />
      )}
      <img
        {...rest}
        src={currentSrc}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        className={cn(
          !eager && 'transition-opacity duration-500',
          !eager && (loaded ? 'opacity-100' : 'opacity-0'),
          className,
        )}
      />
    </span>
  );
}
